import type { CmmdRuntimeThreadReplica, TurnId } from "@cmmd-forge/contracts";
import { decodeCmmdRuntimePlanActivity } from "@cmmd-forge/contracts";

import type { ActivePlanState } from "../../session-logic";
import { compareReplicaEvents } from "./remoteRuntimeTimelineEntries";

/**
 * Rebuilds the composer plan banner state from the durable replica. A plan
 * update rides the `tool_call` event kind because the forge-context contract
 * has no plan event kind, the same trade the worktree launch rows make
 * (#3319). The most recently replicated plan wins, in replica order.
 */
export function deriveRemotePlanProgress(
  replica: CmmdRuntimeThreadReplica | null,
): ActivePlanState | null {
  const planEvents = (replica?.events ?? []).toSorted(compareReplicaEvents).flatMap((event) => {
    if (event.kind !== "tool_call") return [];
    const decoded = decodeCmmdRuntimePlanActivity(event.payload);
    return decoded === null
      ? []
      : [{ turnId: event.turnId, createdAt: event.occurredAt, plan: decoded.plan }];
  });
  const latest = planEvents.findLast((planEvent) => planEvent.plan.length > 0);
  if (!latest) return null;
  return {
    createdAt: latest.createdAt,
    turnId: (latest.turnId ?? null) as TurnId | null,
    steps: latest.plan.map((step) => ({ step: step.step, status: step.status })),
  };
}

/**
 * The turn the composer banner should read as current, and whether it has
 * settled, from the durable replica alone. The Forge serializer never emits
 * turn_status events, so the thread status is the working signal: "running"
 * while the latest turn is live, "completed" or "failed" afterwards. A
 * turn_status event for the turn wins when one is present. Defaults to
 * settled so a banner can never stay open on a turn the replica stopped
 * reporting on (#3319).
 */
export function deriveRemoteLatestTurn(replica: CmmdRuntimeThreadReplica | null): {
  readonly turnId: TurnId | null;
  readonly settled: boolean;
} {
  const events = (replica?.events ?? []).toSorted(compareReplicaEvents);
  const turnId = (events.findLast((event) => event.turnId != null)?.turnId ??
    null) as TurnId | null;
  if (!turnId) return { turnId: null, settled: true };
  const status = events.findLast(
    (event) => event.kind === "turn_status" && event.turnId === turnId,
  )?.status;
  const settled =
    status != null ? !UNSETTLED_TURN_STATUSES.has(status) : replica?.thread.status !== "running";
  return { turnId, settled };
}

const UNSETTLED_TURN_STATUSES: ReadonlySet<string> = new Set(["pending", "running"]);
